import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { MessageSquare, Send, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * SMB Activation WhatsApp Templates — UI-configurable overrides for the
 * 5 lifecycle templates + 4 reminder templates. Each row shows the
 * Meta-approved template *name* per stage; per-stage variable docs are
 * surfaced below the input so operators can validate placement.
 *
 * Storage: backend `app_settings._id=smb_activation_templates` doc.
 * DB value, when set, takes precedence over `.env`. Clearing the input
 * falls back to the env value (shown as a placeholder).
 */
export const SmbActivationTemplatesCard = () => {
  const [items, setItems] = useState([]);
  const [waConfigured, setWaConfigured] = useState(false);
  const [language, setLanguage] = useState('en');
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Test-send dialog state
  const [testEnvKey, setTestEnvKey] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testVars, setTestVars] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/smb/activation/templates');
      if (r.data?.success) {
        setItems(r.data.data.templates || []);
        setWaConfigured(!!r.data.data.whatsapp_configured);
        setLanguage(r.data.data.language || 'en');
        setDirty({});
      }
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load activation templates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;
  if (!items.length) return null;

  const onChange = (envKey, val) => setDirty(d => ({ ...d, [envKey]: val }));

  const save = async () => {
    if (!Object.keys(dirty).length) return;
    setSaving(true);
    try {
      await api.put('/smb/activation/templates', dirty);
      toast.success('Templates saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openTest = (envKey) => {
    setTestEnvKey(envKey);
    setTestTo('');
    setTestVars('');
    setTestResult(null);
  };

  const runTest = async () => {
    if (!testTo.trim()) {
      toast.error('Enter a recipient phone number');
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    try {
      const sampleVars = testVars.split('|').map(s => s.trim()).filter(Boolean);
      // Pass the unsaved input value as an override so Test works
      // *before* the user clicks "Save changes" on the row. Falls back
      // to DB → env on the server when this is empty.
      const overrideRaw = dirty[testEnvKey] !== undefined
        ? dirty[testEnvKey]
        : (items.find(i => i.env_key === testEnvKey)?.value || '');
      const paramsOverrideRaw = dirty[`${testEnvKey}_params`] !== undefined
        ? dirty[`${testEnvKey}_params`]
        : (items.find(i => i.env_key === testEnvKey)?.param_names || []).join(', ');
      const r = await api.post('/smb/activation/templates/test-send', {
        env_key: testEnvKey,
        to: testTo.trim(),
        sample_vars: sampleVars,
        template_name_override: (overrideRaw || '').trim(),
        param_names_override: (paramsOverrideRaw || '').trim(),
      });
      setTestResult(r.data);
      if (r.data.ok) toast.success(r.data.stub ? 'Sent (stub mode)' : 'Test message sent');
      else toast.error(r.data.error || 'Send failed — see details');
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
      toast.error('Send failed');
    } finally { setTestBusy(false); }
  };

  const hasDirty = Object.keys(dirty).length > 0;

  return (
    <Card data-testid="smb-activation-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-emerald-500" />
          SMB Activation WhatsApp Templates
          {waConfigured ? (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />WhatsApp configured
            </Badge>
          ) : (
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />WhatsApp NOT configured
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Override the Meta-approved template names per activation stage. DB values shown here
          take precedence over <code className="text-[10px] bg-muted px-1 rounded">.env</code>
          values. Leave blank to fall back to env. Language: <span className="font-medium">{language}</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!waConfigured && (
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">WhatsApp credentials are missing.</p>
              <p className="mt-0.5">Set <code className="text-[10px] bg-black/30 px-1 rounded">WHATSAPP_PHONE_NUMBER_ID</code> and
                {' '}<code className="text-[10px] bg-black/30 px-1 rounded">WHATSAPP_ACCESS_TOKEN</code> in <code className="text-[10px] bg-black/30 px-1 rounded">backend/.env</code> and
                restart the backend. Until then template sends will log to stdout but won&apos;t reach Meta.</p>
            </div>
          </div>
        )}

        <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
          {items.map(it => {
            const current = dirty[it.env_key] !== undefined ? dirty[it.env_key] : it.value;
            const effective = current || it.env_fallback;
            return (
              <div key={it.env_key} className="rounded-lg border border-border bg-card/50 p-3 space-y-1.5"
                   data-testid={`tpl-row-${it.env_key}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-semibold">{it.label}</Label>
                    {effective ? (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-400">
                        Not set
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openTest(it.env_key)}
                          disabled={!effective}
                          className="h-7 text-[11px]"
                          data-testid={`tpl-test-${it.env_key}`}>
                    <Send className="h-3 w-3 mr-1" />Test send
                  </Button>
                </div>
                <Input
                  value={current}
                  onChange={e => onChange(it.env_key, e.target.value)}
                  placeholder={it.env_fallback || `e.g. ${it.env_key.toLowerCase()}`}
                  className="h-9 font-mono text-xs"
                  data-testid={`tpl-input-${it.env_key}`}
                />
                {/* Parameter names (named templates only). Comma-separated
                    in the order they appear in the template body. Leave
                    blank for templates that still use {{1}} positional
                    placeholders. */}
                <div>
                  <Label className="text-[10px] text-muted-foreground">
                    Parameter names (comma-separated, in order — leave blank for positional <code className="bg-muted/50 px-1 rounded">{'{{1}}'}</code>)
                  </Label>
                  <Input
                    value={dirty[`${it.env_key}_params`] !== undefined
                      ? dirty[`${it.env_key}_params`]
                      : (it.param_names || []).join(', ')}
                    onChange={e => onChange(`${it.env_key}_params`, e.target.value)}
                    placeholder={
                      it.env_key === 'SMB_ACTIVATION_TPL_NAME'      ? 'activation_link, activation_fee' :
                      it.env_key === 'SMB_ACTIVATION_PAID_TPL_NAME' ? 'order_id' :
                      it.env_key === 'SMB_LIVE_PREVIEW_TPL_NAME'    ? 'preview_url' :
                      it.env_key === 'SMB_FINAL_PAYMENT_TPL_NAME'   ? 'balance_amount, payment_url' :
                      it.env_key === 'SMB_WEBSITE_LIVE_TPL_NAME'    ? 'live_url' :
                      it.env_key.startsWith('SMB_REMINDER_R3')      ? 'activation_link' :
                      it.env_key.startsWith('SMB_REMINDER_R4')      ? 'preview_url' :
                      it.env_key.startsWith('SMB_REMINDER_R')       ? 'activation_link, activation_fee' :
                      'param1, param2'
                    }
                    className="h-8 font-mono text-[11px]"
                    data-testid={`tpl-params-${it.env_key}`}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground gap-2 flex-wrap">
                  <code className="bg-muted/50 px-1 py-0.5 rounded">{it.env_key}</code>
                  <span>Vars: {it.vars.join(' · ')}</span>
                  {it.env_fallback && current === '' && (
                    <span className="italic">env fallback: {it.env_fallback}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={!hasDirty || saving} data-testid="tpl-save">
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Save changes'}
          </Button>
        </div>

        {/* Inline test-send panel */}
        {testEnvKey && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 mt-3 space-y-2"
               data-testid="tpl-test-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-emerald-400">
                Test send → {items.find(i => i.env_key === testEnvKey)?.label}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setTestEnvKey(null)} className="h-6 text-[10px]">
                Close
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Recipient phone (E.164)</Label>
                <Input value={testTo} onChange={e => setTestTo(e.target.value)}
                       placeholder="+919876543210" className="h-8 text-xs"
                       data-testid="tpl-test-to" />
              </div>
              <div>
                <Label className="text-[10px]">Sample variables (pipe-separated)</Label>
                <Input value={testVars} onChange={e => setTestVars(e.target.value)}
                       placeholder="https://test.com|₹999" className="h-8 text-xs"
                       data-testid="tpl-test-vars" />
              </div>
            </div>
            <Button onClick={runTest} disabled={testBusy} size="sm" className="w-full"
                    data-testid="tpl-test-run">
              {testBusy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Sending…</> :
                <><Send className="h-3 w-3 mr-1" />Send test</>}
            </Button>
            {testResult && (
              <div className={`rounded p-2 text-[11px] font-mono ${
                testResult.ok ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'
              }`} data-testid="tpl-test-result">
                <div>ok: {String(testResult.ok)}</div>
                {testResult.template_name && <div>tpl: {testResult.template_name}</div>}
                {testResult.message_id && <div>msg_id: {testResult.message_id}</div>}
                {testResult.stub && <div>mode: STUB (WhatsApp not configured)</div>}
                {testResult.error && <div className="break-all">err: {testResult.error}</div>}
                {testResult.delivery_status && (
                  <div className="mt-1 pt-1 border-t border-current/20">
                    delivery: <span className="font-semibold">{testResult.delivery_status}</span>
                    {testResult.delivery_updated_at && (
                      <span className="opacity-70 ml-2">
                        ({new Date(testResult.delivery_updated_at).toLocaleTimeString()})
                      </span>
                    )}
                    {testResult.delivery_error && (
                      <div className="break-all opacity-80">{testResult.delivery_error}</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {testResult?.message_id && (
              <Button
                onClick={async () => {
                  try {
                    const r = await api.get(`/smb/activation/whatsapp/status/${encodeURIComponent(testResult.message_id)}`);
                    if (r.data?.data) {
                      setTestResult(prev => ({
                        ...prev,
                        delivery_status: r.data.data.delivery_status,
                        delivery_updated_at: r.data.data.delivery_updated_at,
                        delivery_error: r.data.data.delivery_error,
                      }));
                      toast.success(`Status: ${r.data.data.delivery_status}`);
                    } else {
                      toast.info(r.data?.hint || 'No delivery status yet — Meta may take 30s-5min to report.');
                    }
                  } catch (e) {
                    toast.error('Status check failed');
                  }
                }}
                size="sm" variant="outline" className="w-full text-[11px]"
                data-testid="tpl-test-check-status">
                Refresh delivery status
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmbActivationTemplatesCard;
